import { OnInit, Component, Input, OnDestroy, Inject } from '@angular/core';

import { filter, Observable, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { isEqual } from 'lodash';

import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { PersonalizationServiceInterface } from '../../services/personalization-service.interface';

import { MergeTagModel } from '../../../../Discover/Core/models/merge-tag.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MergeTagService } from '../../../../Discover/Targeting/services/merge-tags.service';
import { PersonalizationTagEnum } from '../../../../Discover/Targeting/enums/personalization-tag.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';


interface MergeTagDataRow {
    name: string;
    mergeTag: MergeTagModel;
    textTag: string;
    fallbackString: string;
    editDisabled: boolean;
}

@Component({
    selector: 'agpr-fallback-personalizations-dialog',
    templateUrl: './fallback-personalizations-dialog.component.html',
    styleUrls: ['./fallback-personalizations-dialog.component.scss'],
    standalone: false
})
export class FallbackPersonalizationsDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() id: string = 'fallbackPersonalizationDialog';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() saveOnComplete: boolean = false;

    dataRows: MergeTagDataRow[] = [];

    readonly buttonType = ButtonTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly personalizationTag = PersonalizationTagEnum;

    private subscriptions: Subscription[] = [];
    originalMergeTags: MergeTagModel[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        @Inject('DistributionService') public mergeTagService: MergeTagService,
        @Inject('PersonalizationServiceInterface') public personalizationService: PersonalizationServiceInterface,
    ) {
        super(coreDialogService, 'FallbackPersonalizationsDialog');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.mergeTagService.getAvailableMergeTags().pipe(
                filter(x => !!x)
            ).subscribe(
                mergeTags => {
                    this.dataRows = [];
                    this.dataRows = mergeTags.map(mergeTag => {
                        return {
                            mergeTag: mergeTag,
                            name: this.localizationService.transform(mergeTag.label),
                            textTag: `[%${mergeTag.key}%]`,
                            fallbackString: mergeTag.fallback?.key,
                            editDisabled: true
                        };
                    });

                    this.originalMergeTags = this.dataRows.map(row =>
                        new MergeTagModel({
                            key: row.mergeTag.key,
                            fallback: { key: row.fallbackString || '' }
                        })
                    );
                }
            )
        );
    }

    openEditDialog(row: MergeTagDataRow) {
        const io = {
            inputs: {
                animationTrigger: this.animationTrigger,
                overlayContainer: this.overlayContainer,
                personalizationType: row.mergeTag.key,
                content: row.fallbackString || '',
            },
            outputs: {
                contentChanges: (text: string) => {
                    row.fallbackString = text;
                }
            }
        };

        this.coreDialogService.open('RichTextEditorDialogComponent', io);
    }

    /** Add the fallback mergetags to the focused distribution */
    saveFallback() {
        const mergeTags: MergeTagModel[] = [];
        this.dataRows.forEach(row => {
            mergeTags.push(new MergeTagModel(
                {
                    key: row.mergeTag.key,
                    fallback: { key: row.fallbackString || '' } // @TODO this is weird
                }
            ));
        });
        const isValid = this.validateMergeTagSizes(mergeTags);
        if (isValid) {
            if (!isEqual(this.originalMergeTags, mergeTags)) {
                this.mergeTagService.updateFallbackTags(mergeTags, this.saveOnComplete);
                this.closeDialog(true);
            }
            this.closeDialog();
        }
    }

    fallbackTextChanged(row: MergeTagDataRow, text: string) {
        row.fallbackString = text;
        this.dataRows.splice(this.dataRows.findIndex(x => x.textTag === row.textTag), 1, row);
    }

    private validateMergeTagSizes(mergeTags: MergeTagModel[]): boolean {
        for (const mergeTag of mergeTags) {
            const limit = this.personalizationService.getSizeLimitForPersonalization({
                tagType: PersonalizationTagEnum[mergeTag.key],
                value: mergeTag.fallback?.key
            });
            if (mergeTag.fallback?.key?.length > limit) {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.distribution.personalizationSaved.error.heading',
                    detail: this.localizationService.transform(
                        'Discover.distribution.personalizationSaved.error.size.body',
                        {
                            tag: PersonalizationTagEnum[mergeTag.key],
                            size: limit
                        }

                    )
                });
                return false;
            }
        }
        return true;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
